import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Callout, Dialog, Form } from "@sscs/ui";
import { useDialog } from "Hooks";
import * as yup from "Utils/yup";
import { CreateInsightsSearchRequest } from "../../types";
import styles from "./unsavedChanges.module.scss";

type CreateSearchFormData = Omit<
  CreateInsightsSearchRequest["body"],
  "filters"
>;

const createSearchSchema: yup.SchemaOf<CreateSearchFormData> = yup.object({
  name: yup.string().required("New search name is required"),
});

type SaveChangesProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (searchName: string) => void;
};

const SaveChangesContent = ({
  onClose,
  onSave,
}: Omit<SaveChangesProps, "isOpen">): React.JSX.Element => {
  const methods = useForm<CreateSearchFormData>({
    resolver: yupResolver(createSearchSchema),
    defaultValues: {
      name: "",
    },
  });
  const {
    formState: { errors },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<CreateSearchFormData> = async (data) => {
    onSave(data.name);
  };

  return (
    <FormProvider {...methods}>
      <Form.Form onSubmit={handleSubmit(onSubmit)}>
        <Dialog.Header>
          <Dialog.Header.Title>Save new search</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Description>
              Enter new name for search:
            </Dialog.Section.Description>
            <Dialog.Section.Content>
              <Form.TextField
                name="name"
                label="New search name"
                id="new-search-name"
                isRequired
                errors={errors}
              />
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button id="cancel" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button id="rename-search" type="submit" color="primary">
              Save
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const SaveChanges = ({
  isOpen,
  ...props
}: SaveChangesProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose} size="xs">
    <SaveChangesContent {...props} />
  </Dialog.Frame>
);

type UnsavedChangesProps = {
  canUpdateSearch: boolean;
  onSave: (searchName: string) => void;
  onUpdate: () => void;
};

const UnsavedChanges = ({
  canUpdateSearch,
  onSave,
  onUpdate,
}: UnsavedChangesProps): React.JSX.Element => {
  const saveSearchDialog = useDialog();

  return (
    <>
      <div className={styles["unsaved-changes"]}>
        <Callout
          type="error"
          title="You have unsaved changes"
          variant="inline"
        />
        {canUpdateSearch && (
          <Button color="secondary" onClick={onUpdate}>
            Update search
          </Button>
        )}
        <Button onClick={() => saveSearchDialog.open()}>
          {canUpdateSearch ? "Save as new search" : "Save search"}
        </Button>
      </div>
      <SaveChanges
        isOpen={saveSearchDialog.isOpen}
        onClose={saveSearchDialog.close}
        onSave={onSave}
      />
    </>
  );
};

export default UnsavedChanges;
